import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  PaginationComponent,
  type PaginationEvent,
  SignalButton,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { AddContactGroupMembersApiResponse } from '@core/api/workspace/models/addContactGroupMembersApiResponse';
import type { ContactResponse } from '@core/api/workspace/models/contactResponse';
import type { ListContactsApiResponse } from '@core/api/workspace/models/listContactsApiResponse';
import type { RemoveContactGroupMemberApiResponse } from '@core/api/workspace/models/removeContactGroupMemberApiResponse';
import {
  resolveAvatarColorClass,
  resolveInitials,
} from '@core/utils/initial-avatar.util';

export interface AddMembersOverlayData {
  groupId: string;
  groupName?: string;
  initialMembers?: Array<
    Pick<ContactResponse, 'id' | 'firstName' | 'lastName' | 'email'>
  >;
}

export interface AddMembersOverlayResult {
  addedCount: number;
  removedCount: number;
}

@Component({
  selector: 'app-add-members-overlay',
  standalone: true,
  imports: [NgClass, PaginationComponent, SignalButton, SignalTextField],
  templateUrl: './add-members-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMembersOverlayComponent extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly dialogRef = inject(
    DialogRef<AddMembersOverlayResult | null>,
  );
  private readonly data = inject<AddMembersOverlayData>(DIALOG_DATA);

  readonly listState = new State<ListContactsApiResponse>();

  readonly groupName = this.data.groupName?.trim() || 'this group';
  readonly search = signal('');
  readonly contacts = signal<ContactResponse[]>([]);
  readonly membersById = signal<Map<string, ContactResponse>>(
    new Map(
      (this.data.initialMembers ?? []).map((member) => [
        member.id,
        {
          id: member.id,
          firstName: member.firstName,
          lastName: member.lastName,
          email: member.email,
        } as ContactResponse,
      ]),
    ),
  );
  readonly pendingIds = signal<Set<string>>(new Set());
  readonly pageNumber = signal(1);
  readonly pageSize = signal(50);
  readonly totalCount = signal(0);
  readonly paginationKey = signal(0);

  readonly members = computed(() => [...this.membersById().values()]);
  readonly memberCount = computed(() => this.membersById().size);

  private addedCount = 0;
  private removedCount = 0;

  ngOnInit(): void {
    this.loadContacts();
  }

  close(): void {
    this.done();
  }

  done(): void {
    if (this.addedCount > 0 || this.removedCount > 0) {
      this.dialogRef.close({
        addedCount: this.addedCount,
        removedCount: this.removedCount,
      });
      return;
    }
    this.dialogRef.close(null);
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    this.pageNumber.set(1);
    this.paginationKey.update((key) => key + 1);
    this.loadContacts();
  }

  onPageChange(event: PaginationEvent): void {
    this.pageNumber.set(event.pageNumber);
    this.pageSize.set(event.pageSize);
    this.loadContacts();
  }

  isMember(id: string): boolean {
    return this.membersById().has(id);
  }

  isPending(id: string): boolean {
    return this.pendingIds().has(id);
  }

  displayName(contact: ContactResponse): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  }

  initials(contact: ContactResponse): string {
    return resolveInitials({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
    });
  }

  avatarColor(id: string): string {
    return resolveAvatarColorClass(id);
  }

  addMember(contact: ContactResponse): void {
    if (this.isMember(contact.id) || this.isPending(contact.id)) {
      return;
    }

    this.setPending(contact.id, true);

    this.executeRequest({
      state: new State<AddContactGroupMembersApiResponse>(),
      request: this.userAPIService.addContactGroupMembers(this.data.groupId, {
        contactIds: [contact.id],
      }),
      handleError: true,
      onSuccess: (response) => {
        const added = response.data?.addedCount ?? 0;
        if (added > 0 || (response.data?.skippedCount ?? 0) > 0) {
          this.membersById.update((members) => {
            const next = new Map(members);
            next.set(contact.id, contact);
            return next;
          });
        }
        if (added > 0) {
          this.addedCount += added;
          this.toasterService.success('Contact added to group.');
        } else {
          this.toasterService.success('Contact is already in the group.');
        }
        this.setPending(contact.id, false);
      },
      onFailed: () => {
        this.setPending(contact.id, false);
      },
    });
  }

  removeMember(contact: ContactResponse): void {
    if (!this.isMember(contact.id) || this.isPending(contact.id)) {
      return;
    }

    this.setPending(contact.id, true);

    this.executeRequest({
      state: new State<RemoveContactGroupMemberApiResponse>(),
      request: this.userAPIService.removeContactGroupMember(
        this.data.groupId,
        contact.id,
      ),
      handleError: true,
      onSuccess: () => {
        this.membersById.update((members) => {
          const next = new Map(members);
          next.delete(contact.id);
          return next;
        });
        this.removedCount += 1;
        this.toasterService.success('Contact removed from group.');
        this.setPending(contact.id, false);
      },
      onFailed: () => {
        this.setPending(contact.id, false);
      },
    });
  }

  private setPending(id: string, pending: boolean): void {
    this.pendingIds.update((ids) => {
      const next = new Set(ids);
      if (pending) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  private loadContacts(): void {
    const search = this.search().trim();

    this.executeRequest({
      state: this.listState,
      request: this.userAPIService.listContacts({
        ...(search ? { search } : {}),
        pageNumber: this.pageNumber(),
        pageSize: this.pageSize(),
      }),
      handleError: true,
      onSuccess: (response) => {
        this.contacts.set(response.data?.items ?? []);
        this.totalCount.set(response.data?.totalCount ?? 0);
      },
    });
  }
}
